// utils/imageCompression.ts

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
}

export const compressImage = (
  base64Image: string,
  options: CompressionOptions = {}
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 1024,
      maxHeight = 1024,
      quality = 0.7,
      maxSizeKB = 500,
    } = options;

    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        // Create canvas and draw compressed image
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels to meet size requirements
        let compressedBase64 = canvas.toDataURL("image/jpeg", quality);
        let currentQuality = quality;

        // If still too large, reduce quality further
        while (
          getBase64SizeKB(compressedBase64) > maxSizeKB &&
          currentQuality > 0.1
        ) {
          currentQuality -= 0.1;
          compressedBase64 = canvas.toDataURL("image/jpeg", currentQuality);
        }

        resolve(compressedBase64);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = base64Image.startsWith("data:")
      ? base64Image
      : `data:image/jpeg;base64,${base64Image}`;
  });
};

export const getBase64SizeKB = (base64String: string): number => {
  const base64Data = base64String.split(",")[1] || base64String;
  const sizeInBytes = (base64Data.length * 3) / 4;
  return sizeInBytes / 1024;
};

export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};
